import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, RotateCcw, BarChart3, HelpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';

interface ProbabilityWheelGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface WheelResult {
  color: 'red' | 'blue' | 'green';
  count: number;
}

interface Question {
  type: 'simple' | 'multiple' | 'comparison';
  question: string;
  options?: string[];
  correctAnswer: string | number;
  explanation: string;
}

const ProbabilityWheelGame: React.FC<ProbabilityWheelGameProps> = ({ mode, onReset, language }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [results, setResults] = useState<WheelResult[]>([
    { color: 'red', count: 0 },
    { color: 'blue', count: 0 },
    { color: 'green', count: 0 }
  ]);
  const [totalSpins, setTotalSpins] = useState(0);
  const [gameMode, setGameMode] = useState<'exploration' | 'exercise'>('exploration');
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const wheelRef = useRef<HTMLDivElement>(null);
  const [playSuccess] = useSound(successSound, { volume: 0.5 });

  // Segments de la roue : 50% rouge, 30% bleu, 20% vert
  const segments = [
    { color: 'red', percentage: 50, angle: 180 },
    { color: 'blue', percentage: 30, angle: 108 },
    { color: 'green', percentage: 20, angle: 72 }
  ];

  const generateQuestion = (): Question => {
    const questionTypes = ['simple', 'multiple', 'comparison'];
    const type = questionTypes[Math.floor(Math.random() * questionTypes.length)] as Question['type'];

    switch (type) {
      case 'simple':
        const colors = ['rouge', 'bleu', 'vert'];
        const percentages = [50, 30, 20];
        const colorIndex = Math.floor(Math.random() * 3);
        return {
          type,
          question: language === 'en' 
            ? `What is the probability of getting ${colors[colorIndex]}?`
            : `Quelle est la probabilité d'obtenir ${colors[colorIndex]} ?`,
          options: ['10%', '20%', '30%', '50%'],
          correctAnswer: `${percentages[colorIndex]}%`,
          explanation: language === 'en'
            ? `The ${colors[colorIndex]} segment represents ${percentages[colorIndex]}% of the wheel.`
            : `Le segment ${colors[colorIndex]} représente ${percentages[colorIndex]}% de la roue.`
        };

      case 'multiple':
        const spins = [10, 20, 50][Math.floor(Math.random() * 3)];
        const expectedRed = Math.round(spins * 0.5);
        return {
          type,
          question: language === 'en'
            ? `If you spin ${spins} times, how many red results do you expect?`
            : `Si vous faites ${spins} tours, combien de résultats rouges attendez-vous ?`,
          correctAnswer: expectedRed,
          explanation: language === 'en'
            ? `Red has 50% probability, so ${spins} × 0.5 = ${expectedRed} expected red results.`
            : `Rouge a 50% de probabilité, donc ${spins} × 0,5 = ${expectedRed} résultats rouges attendus.`
        };

      case 'comparison':
        return {
          type,
          question: language === 'en'
            ? 'Which color is most likely to appear?'
            : 'Quelle couleur a le plus de chances d\'apparaître ?',
          options: ['Rouge', 'Bleu', 'Vert', 'Toutes égales'],
          correctAnswer: 'Rouge',
          explanation: language === 'en'
            ? 'Red has the highest probability at 50%.'
            : 'Rouge a la plus haute probabilité avec 50%.'
        };

      default:
        return generateQuestion();
    }
  };

  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    const spinAmount = 1440 + Math.random() * 1440; // 4-8 tours
    const newRotation = rotation + spinAmount;
    setRotation(newRotation);

    setTimeout(() => {
      // Calculer le résultat basé sur l'angle final
      const finalAngle = newRotation % 360;
      let result: 'red' | 'blue' | 'green';
      
      if (finalAngle >= 0 && finalAngle < 180) {
        result = 'red';
      } else if (finalAngle >= 180 && finalAngle < 288) {
        result = 'blue';
      } else {
        result = 'green';
      }

      // Mettre à jour les résultats
      setResults(prev => prev.map(r => 
        r.color === result ? { ...r, count: r.count + 1 } : r
      ));
      setTotalSpins(prev => prev + 1);
      setIsSpinning(false);

      if (gameMode === 'exercise' && !currentQuestion) {
        setCurrentQuestion(generateQuestion());
      }
    }, 3000);
  };

  const handleAnswer = (answer: string | number) => {
    if (!currentQuestion || showFeedback) return;

    setUserAnswer(answer.toString());
    setShowFeedback(true);
    
    const correct = answer.toString() === currentQuestion.correctAnswer.toString();
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      setScore(score + 1);
      playSuccess();
    }

    setTimeout(() => {
      setQuestionCount(questionCount + 1);
      if (questionCount >= 9) {
        saveProgress(newStats);
        setShowStats(true);
      } else {
        setCurrentQuestion(generateQuestion());
        setShowFeedback(false);
        setUserAnswer('');
      }
    }, 2000);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'probability',
        mode: 'wheel',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setResults([
      { color: 'red', count: 0 },
      { color: 'blue', count: 0 },
      { color: 'green', count: 0 }
    ]);
    setTotalSpins(0);
    setScore(0);
    setQuestionCount(0);
    setCurrentQuestion(null);
    setShowFeedback(false);
    setShowStats(false);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
  };

  const getColorName = (color: string) => {
    const names = {
      red: language === 'en' ? 'Red' : 'Rouge',
      blue: language === 'en' ? 'Blue' : 'Bleu',
      green: language === 'en' ? 'Green' : 'Vert'
    };
    return names[color as keyof typeof names];
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => setGameMode(gameMode === 'exploration' ? 'exercise' : 'exploration')}
            className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            {gameMode === 'exploration' 
              ? (language === 'en' ? 'Exercise Mode' : 'Mode Exercice')
              : (language === 'en' ? 'Exploration Mode' : 'Mode Exploration')}
          </button>
          
          <button
            onClick={resetGame}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {showStats ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <BarChart3 className="h-16 w-16 text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Exercise Complete!' : 'Exercice terminé !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{score}/10</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Score' : 'Score'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {Math.round((score / 10) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Roue des probabilités */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-6 text-center">
              {language === 'en' ? 'Probability Wheel' : 'Roue des Probabilités'}
            </h2>
            
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div
                  ref={wheelRef}
                  className="w-64 h-64 rounded-full border-4 border-slate-300 dark:border-slate-600 relative overflow-hidden transition-transform duration-3000 ease-out"
                  style={{ transform: `rotate(${rotation}deg)` }}
                >
                  {/* Segment rouge (50%) */}
                  <div 
                    className="absolute inset-0 bg-red-500"
                    style={{
                      clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%)'
                    }}
                  />
                  {/* Segment bleu (30%) */}
                  <div 
                    className="absolute inset-0 bg-blue-500"
                    style={{
                      clipPath: 'polygon(50% 50%, 0% 0%, 50% 0%)',
                      transform: 'rotate(180deg)'
                    }}
                  />
                  {/* Segment vert (20%) */}
                  <div 
                    className="absolute inset-0 bg-green-500"
                    style={{
                      clipPath: 'polygon(50% 50%, 0% 100%, 0% 0%)',
                      transform: 'rotate(180deg)'
                    }}
                  />
                  
                  {/* Centre de la roue */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 bg-white dark:bg-slate-800 rounded-full border-2 border-slate-400"></div>
                  </div>
                </div>
                
                {/* Pointeur */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2">
                  <div className="w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-slate-800 dark:border-b-slate-200"></div>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <button
                onClick={spinWheel}
                disabled={isSpinning}
                className="px-8 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium"
              >
                {isSpinning 
                  ? (language === 'en' ? 'Spinning...' : 'Rotation...')
                  : (language === 'en' ? 'Spin the Wheel!' : 'Faire tourner !')}
              </button>
            </div>

            {/* Légende */}
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-slate-700 dark:text-slate-300">50%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-slate-700 dark:text-slate-300">30%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-slate-700 dark:text-slate-300">20%</span>
              </div>
            </div>
          </div>

          {/* Statistiques et questions */}
          <div className="space-y-6">
            {/* Résultats */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                {language === 'en' ? 'Results' : 'Résultats'}
              </h3>
              
              <div className="space-y-3">
                {results.map((result) => (
                  <div key={result.color} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${
                        result.color === 'red' ? 'bg-red-500' :
                        result.color === 'blue' ? 'bg-blue-500' : 'bg-green-500'
                      }`}></div>
                      <span className="text-slate-700 dark:text-slate-300">
                        {getColorName(result.color)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {result.count}
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {totalSpins > 0 ? Math.round((result.count / totalSpins) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    {language === 'en' ? 'Total spins:' : 'Tours totaux :'}
                  </span>
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    {totalSpins}
                  </span>
                </div>
              </div>
            </div>

            {/* Questions en mode exercice */}
            {gameMode === 'exercise' && currentQuestion && (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <HelpCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                    {language === 'en' ? 'Question' : 'Question'} {questionCount + 1}/10
                  </h3>
                </div>
                
                <p className="text-slate-700 dark:text-slate-300 mb-4">
                  {currentQuestion.question}
                </p>

                {currentQuestion.options ? (
                  <div className="grid grid-cols-2 gap-2">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option)}
                        disabled={showFeedback}
                        className={`p-3 rounded-lg border transition-colors ${
                          showFeedback
                            ? option === currentQuestion.correctAnswer
                              ? 'bg-green-100 dark:bg-green-900/50 border-green-500 text-green-700 dark:text-green-300'
                              : option === userAnswer
                              ? 'bg-red-100 dark:bg-red-900/50 border-red-500 text-red-700 dark:text-red-300'
                              : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600'
                            : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/50'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      className="flex-1 p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      placeholder={language === 'en' ? 'Enter your answer' : 'Votre réponse'}
                      disabled={showFeedback}
                    />
                    <button
                      onClick={() => handleAnswer(parseInt(userAnswer))}
                      disabled={!userAnswer || showFeedback}
                      className="px-4 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
                    >
                      {language === 'en' ? 'Submit' : 'Valider'}
                    </button>
                  </div>
                )}

                {showFeedback && (
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {currentQuestion.explanation}
                    </p>
                  </div>
                )}

                <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                  {language === 'en' ? 'Score:' : 'Score :'} {score}/{questionCount + 1}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbabilityWheelGame;